import type { ReadingCompanionProps } from "@op/puck-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cls,
  clx,
  useIsDesktop,
  useLayerCitations,
  useLayerFigures,
  useLayerOutline,
  useTheme,
  useTranslation,
} from "@op/utils";
import { Dc_Reading, IconButton } from "@op/sf-op";
import {
  FeatherArrowLeftToLine,
  FeatherArrowRightToLine,
  FeatherInfo,
  FeatherMoon,
  FeatherSun,
} from "@subframe/core";
import { useRender } from "@op/utils";
import { ShareButton } from "../../../../../components/base/ShareButton";
import { ReadingCompanionSegmentItem } from "./ReadingCompanionSegmentItem";
import { ReadingCompanionOutline } from "./ReadingCompanionOutline";
import { ReadingCompanionCitations } from "./ReadingCompanionCitations";
import { ReadingCompanionFigures } from "./ReadingCompanionFigures";
import LayoutRow from "./LayoutRow";

type OpenSegment = "outline" | "citations" | "figures" | null;

const TRIGGER_HEIGHT = 52 as const;
const TOP_HEIGHT = 138 as const;
const TOP_OFFSET = 32 as const;

const ReadingCompanion = ({ onMetaIntroOpenChange }: ReadingCompanionProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpenPreferred, setIsOpenPreferred] = useState<boolean>(false);
  const [currentlyOpenSegment, setCurrentlyOpenSegment] =
    useState<OpenSegment>(null);

  const outline = useLayerOutline();
  const citations = useLayerCitations();
  const figures = useLayerFigures();

  const segmentLength = useMemo(() => {
    let length = 0;
    if (outline) length++;
    if (citations) length++;
    if (figures) length++;
    return length;
  }, [outline, figures, citations]);

  const segmentMaxHeight = useMemo(
    () => segmentLength * TRIGGER_HEIGHT + TOP_HEIGHT + TOP_OFFSET * 2,
    [segmentLength]
  );

  const isDesktop = useIsDesktop();
  useEffect(() => {
    setIsOpenPreferred(isDesktop);
  }, [isDesktop]);

  function toggleOpen() {
    setIsOpenPreferred((prev) => !prev);
  }

  const { readingCompanion: trans } = useTranslation();

  const onOpenMetaIntro = useCallback(
    (open: boolean) => {
      onMetaIntroOpenChange?.(open);
    },
    [onMetaIntroOpenChange]
  );

  const { darkMode, setDarkMode } = useTheme();

  const { context } = useRender();
  const isNotRenderer = useMemo(() => context !== "renderer", [context]);

  const isOpen = useMemo(
    () => (isNotRenderer ? false : isOpenPreferred),
    [isOpenPreferred, isNotRenderer]
  );

  if (isNotRenderer) return null;
  return (
    <div
      className={cls(
        "sticky top-4 z-20 left-0 h-0 pointer-events-none",
        clx({
          "w-full": isOpen,
        })
      )}
    >
      <LayoutRow
        variant="narrow"
        leftChildren={
          <Dc_Reading
            ref={containerRef}
            className={cls(
              "pointer-events-auto inline-block max-h-[calc(100dvh-40px)]",
              clx({
                "w-full min-w-[200px]": isOpen,
              })
            )}
            headerSlot={
              <Dc_Reading.HeaderStack
                isMinimized={!isOpen}
                expandButtonSlot={
                  <IconButton
                    variant="brand-primary"
                    disabled={isNotRenderer}
                    onClick={toggleOpen}
                    icon={
                      isOpen ? (
                        <FeatherArrowLeftToLine />
                      ) : (
                        <FeatherArrowRightToLine />
                      )
                    }
                  />
                }
                actionSlot={
                  <>
                    <IconButton
                      variant="neutral-secondary"
                      disabled={isNotRenderer}
                      onClick={() => onOpenMetaIntro(true)}
                      icon={<FeatherInfo />}
                    />
                    <IconButton
                      variant="neutral-secondary"
                      disabled={isNotRenderer}
                      onClick={() => setDarkMode(!darkMode)}
                      icon={darkMode ? <FeatherSun /> : <FeatherMoon />}
                    />
                    <ShareButton />
                  </>
                }
              />
            }
            contentStack={
              isOpen && (
                <>
                  {outline.length > 0 && (
                    <ReadingCompanionSegmentItem
                      maxHeight={segmentMaxHeight}
                      open={currentlyOpenSegment === "outline"}
                      onOpenChange={(open) =>
                        open
                          ? setCurrentlyOpenSegment("outline")
                          : setCurrentlyOpenSegment(null)
                      }
                      title={trans.outline}
                    >
                      <ReadingCompanionOutline outline={outline} />
                    </ReadingCompanionSegmentItem>
                  )}
                  {citations.length > 0 && (
                    <ReadingCompanionSegmentItem
                      maxHeight={segmentMaxHeight}
                      open={currentlyOpenSegment === "citations"}
                      onOpenChange={(open) =>
                        open
                          ? setCurrentlyOpenSegment("citations")
                          : setCurrentlyOpenSegment(null)
                      }
                      title={trans.citations}
                    >
                      <ReadingCompanionCitations citations={citations} />
                    </ReadingCompanionSegmentItem>
                  )}
                  {figures.length > 0 && (
                    <ReadingCompanionSegmentItem
                      maxHeight={segmentMaxHeight}
                      open={currentlyOpenSegment === "figures"}
                      onOpenChange={(open) =>
                        open
                          ? setCurrentlyOpenSegment("figures")
                          : setCurrentlyOpenSegment(null)
                      }
                      title={trans.figures}
                    >
                      <ReadingCompanionFigures figures={figures} />
                    </ReadingCompanionSegmentItem>
                  )}
                </>
              )
            }
          />
        }
      />
    </div>
  );
};

export default ReadingCompanion;
